import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Spinner,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserGraduate,
} from "@fortawesome/free-solid-svg-icons";
import BirthdaySidebar from "../sidebar/Birthday";
import StudentsFilter from "./Filter";
import ListDisplay from "./ListDisplay";
import CoffeeSignal from "./CoffeeSignal";
import PaginationBar from "./PaginationBar";
import { notify } from "react-notify-toast";
import LoadingScreen from "../../../Layout/LoadingScreen";
import { useHistory } from "react-router-dom";
import InternalNotifications from "../../../Layout/InternalNotifications/InternalNotifications";
import { setStudentListContext } from "../../../Context/StudentListContext";
import { getStudents } from "../../../services/studentsService";

export default function StudentList() {
  const history = useHistory();
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumbers, setPageNumbers] = useState([]);
  const [currentQuery, setCurrentQuery] = useState(null);

  const getStudentListFromDatabase = useCallback(
    async (query, page, size) => {
      setLoadingFilter(true);
      setCurrentQuery(query);
      try {
        const response = await getStudents({ page: page || 1, pageSize: size || pageSize, query });
        setStudents(response.data || []);
        setCurrentPage(response.currentPage);
        setTotalRecords(response.totalRecords);
        setTotalPages(response.totalPages);
        setPageNumbers(response.pageNumbers || []);
        setStudentListContext(response.data || []);
      } catch (error) {
        if (error?.response) {
          notify.show(error.response.data.message, "error");
        }
        history.push("/");
      } finally {
        setLoadingFilter(false);
        setIsLoading(false);
      }
    },
    [history, pageSize]
  );

  const getStudentListOptions = useCallback(
    (query) => {
      getStudentListFromDatabase(query, 1);
    },
    [getStudentListFromDatabase]
  );

  const clearSearch = useCallback(() => {
    setStudents([]);
    setTotalRecords(0);
    setTotalPages(0);
    setPageNumbers([]);
    setCurrentPage(1);
    setStudentListContext([]);
    setCurrentQuery(null);
  }, []);

  useEffect(() => {
    const currentTimeLocalString = new Date().toLocaleString();
    setLastUpdate(currentTimeLocalString);
  }, [students]);

  const handleRefresh = useCallback(() => {
    getStudentListFromDatabase(currentQuery, currentPage);
  }, [getStudentListFromDatabase, currentQuery, currentPage]);

  const handlePageSizeChange = useCallback((value) => {
    setPageSize(value);
    setCurrentPage(1);
    getStudentListFromDatabase(currentQuery, 1, value);
  }, [getStudentListFromDatabase, currentQuery]);

  const handleNavigate = useCallback((page) => {
    getStudentListFromDatabase(currentQuery, page);
  }, [getStudentListFromDatabase, currentQuery]);

  if (isLoading) {
    return <LoadingScreen customText="Cargando datos de alumnos..." />;
  }

  return (
    <Container fluid="lg" className="py-4">
      <Card className="border-0 shadow-sm rounded-lg mb-4 bg-white">
        <Card.Body className="p-4 d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <div
              className="bg-primary text-white rounded-circle p-3 mr-3 d-flex align-items-center justify-content-center shadow-sm"
              style={{ width: "52px", height: "52px" }}
            >
              <FontAwesomeIcon icon={faUserGraduate} size="lg" />
            </div>
            <div>
              <h4 className="font-weight-bold text-dark mb-1">
                Gestión de Estudiantes
              </h4>
              <p className="text-muted mb-0 small">
                Busca, filtra y administra comisiones y estudiantes
              </p>
            </div>
          </div>
          <div className="mt-3 mt-sm-0">
            <CoffeeSignal />
          </div>
        </Card.Body>
      </Card>
      <InternalNotifications />
      <BirthdaySidebar classroom="general" />

      <Row className="m-0">
        <Col className="p-0">
          <StudentsFilter
            filterStudents={getStudentListOptions}
            clearSearch={clearSearch}
            loadingFilter={loadingFilter}
          />
        </Col>
      </Row>
      <Row className="m-0">
        <Col className="p-0">
          {loadingFilter ? (
            <Card className="border-0 shadow-sm py-5 text-center bg-white my-3">
              <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h6 className="font-weight-bold text-primary mb-1">Cargando resultados...</h6>
                <small className="text-muted">Obteniendo listado de estudiantes</small>
              </Card.Body>
            </Card>
          ) : (
            <ListDisplay
              studentsList={students}
              studentCount={totalRecords}
              refreshList={handleRefresh}
              lastUpdate={lastUpdate}
              pageSize={pageSize}
              setPageSize={handlePageSizeChange}
              currentQuery={currentQuery}
            />
          )}
        </Col>
      </Row>
      {totalPages > 1 && !loadingFilter && (
        <PaginationBar
          pageNumbers={pageNumbers}
          currentPage={currentPage}
          totalPages={totalPages}
          onNavigate={handleNavigate}
        />
      )}
    </Container>
  );
}